import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { steps } from '../constants/configuration';
import ServiceWrapper from './serviceWrapper';
import styles from '../styles/configuration.module.scss';
import InputComponent from './inputComponent';
import { Dropdown } from 'react-dropdown-now';
import 'react-dropdown-now/style.css';

function OpenshiftConfig({ service, changeConfigurations, intialConfigs, ssd, hdd, compute }) {
    const { t } = useTranslation('configuration');

    const [isCheck, setIsCheck] = useState(intialConfigs.isCheck);
    const [nodes, setNodes] = useState(intialConfigs.nodes);
    const [cpu, setCpu] = useState(intialConfigs.cpu);
    const [ram, setRam] = useState(intialConfigs.ram);
    const [isMount, setIsMount] = useState(false);
    const [space, setSpace] = useState(intialConfigs.space);
    const [blockSsd, setBlockSsd] = useState(intialConfigs.blockSsd);
    const [blockHdd, setBlockHdd] = useState(intialConfigs.blockHdd);
    const [filesystem, setFilesystem] = useState(intialConfigs.filesystem);
    const [registry, setRegistry] = useState({
        s3: intialConfigs.s3,
        fs: intialConfigs.fs
    });
    const [isHidden, setIsHidden] = useState(true);
    const [value, setValue] = useState(0);
    const [nodesSelect, setNodesSelect] = useState(service.options[0].values);
    const [cpuSelect, setCpuSelect] = useState(service.options[2].values);
    const [ramSelect, setRamSelect] = useState(service.options[1].values);
    const [presetOptions, setPresetOptions] = useState(steps);

    const checkIfCustom = () => {
        let futureStep = value;
        if (service.options[0][steps[value]] !== nodes || service.options[1][steps[value]] !== ram || service.options[2][steps[value]] !== cpu) {
            futureStep = 3;
        }

        return futureStep;
    };

    const findValue = (nodes) => {
        let degreeIndex = 0;
        steps.forEach((step, index) => {
            if (service.options[0][step] === nodes) {
                degreeIndex = index;
                return;
            }
        });

        if (service.options[1][steps[degreeIndex]] !== ram || service.options[2][steps[degreeIndex]] !== cpu) {
            degreeIndex = 3;
        }

        return degreeIndex;
    };

    useEffect(() => {
        if (isMount) {
            setValue(value === 3 ? findValue(nodes) : checkIfCustom());
        }
    }, [nodes, cpu, ram]);

    useEffect(() => {
        if (value !== 3 && isMount) {
            setNodes(service.options[0][steps[value]]);
            setCpu(service.options[2][steps[value]]);
            setRam(service.options[1][steps[value]]);
        }
    }, [value]);

    useEffect(() => {
        if (isMount) {
            changeConfigurations({ isCheck, nodes, cpu, ram, space, blockSsd, blockHdd, filesystem, s3: registry.s3, fs: registry.fs });
        }
    }, [nodes, ram, space, blockSsd, blockHdd, filesystem, registry, isCheck]);

    useEffect(() => {
        setIsMount(true);
    }, []);

    useEffect(() => {
        if (!isCheck) {
            setSpace(0);
            setBlockSsd(0);
            setBlockHdd(0);
            setFilesystem(0);
            setRegistry({
                s3: 0,
                fs: 0
            });
            setValue(0);
        }
    }, [isCheck]);

    useEffect(() => {
        setNodesSelect(service.options[0].values.filter(item => item.value * cpu <= 2 * compute.cpu * compute.computeNodes && item.value * ram <= compute.ram * compute.computeNodes));
        setCpuSelect(service.options[2].values.filter(item => item.value * nodes <= 2 * compute.cpu * compute.computeNodes) || []);
        setRamSelect(service.options[1].values.filter(item => item.value * nodes <= compute.ram * compute.computeNodes) || []);
    }, [compute, nodes, ram, cpu]);

    useEffect(() => {
        if (2 * compute.cpu * compute.computeNodes >= service.options[0].max * service.options[2].max && compute.ram * compute.computeNodes >= service.options[0].max * service.options[1].max) {
            setPresetOptions(['min', 'mid', 'max', 'custom']);
        } else if (2 * compute.cpu * compute.computeNodes >= service.options[0].mid * service.options[2].mid && compute.ram * compute.computeNodes >= service.options[0].mid * service.options[1].mid) {
            setPresetOptions(['min', 'mid', 'custom']);
        } else {
            setPresetOptions(['min', 'custom']);
        }
    }, [compute]);

    return (
        <ServiceWrapper service={service}
                        value={isCheck}
                        changeActivity={value => setIsCheck(value)}
                        presetOptions={presetOptions}
                        preset={value}
                        changePreset={newValue => newValue !== 3 && setValue(newValue)}>
            <div className={'flex ' + styles.configs} onClick={() => setIsHidden(!isHidden)}>
                <div className={styles['arrow-container']}>
                    <div className={`${styles['arrow-element']} ${isHidden ? '' : styles['rotate-forward']}`} />
                </div>
                <p className={'pointer ' + styles.title}>{t('config')}</p>
            </div>
            <div className={styles[isHidden ? 'hide' : 'visible'] + ' ' + styles.options}>
            <div className={styles['config-wrapper']}>
                    <p className={styles.content}>{t('openshift.nodes')}</p>
                    <Dropdown
                        disabled={!isCheck}
                        options={nodesSelect.map((option) => ({ value: option.value, label: option.text }))}
                        onChange={(e) => setNodes(Number(e.value))}
                        value={nodes}
                        className={styles['dropdown-size']}
                    />
                </div>
                <div className={styles['config-wrapper']}>
                    <p className={styles.content}>{t('openshift.cpu')}</p>
                    <Dropdown
                        disabled={!isCheck}
                        options={cpuSelect.map((option) => ({ value: option.value, label: option.text }))}
                        onChange={(e) => setCpu(Number(e.value))}
                        value={cpu}
                        className={styles['dropdown-size']}
                    />
                </div>
                <InputComponent label={'openshift.space'}
                                initial={space}
                                free={ssd.free - blockSsd - filesystem - registry.s3 - registry.fs}
                                usable={ssd.total / nodes}
                                setSpace={size => setSpace(size)}
                                available={isCheck}
                                style={{ marginBottom: '1rem' }} />
                <div className={styles['config-wrapper']}>
                    <p className={styles.content}>{ t('openshift.ram') }</p>
                    <Dropdown
                        disabled={!isCheck}
                        options={ramSelect.map((option) => ({ value: option.value, label: option.text }))}
                        onChange={(e) => setRam(Number(e.value))}
                        value={ram}
                        className={styles['dropdown-size']}
                    />
                </div>
            </div>
            <div className={styles['compute-type']}>
                <div className={styles[isHidden ? 'hide' : 'visible']}>
                    <p className={styles.title}>{t('openshift.appPersistent')}</p>
                    <InputComponent label={'openshift.blockSsd'}
                                            initial={blockSsd}
                                            free={ssd.free - space - filesystem - registry.s3 - registry.fs}
                                            usable={ssd.total}
                                            setSpace={size => setBlockSsd(size)}
                                            checkBox
                                            available={isCheck} />
                    { hdd && <InputComponent label={'openshift.blockHdd'}
                            initial={blockHdd}
                            free={hdd.free}
                            usable={hdd.total}
                            setSpace={size => setBlockHdd(size)}
                            checkBox
                            available={isCheck} /> }
                    <InputComponent label={'openshift.filesystem'}
                            initial={filesystem}
                            free={ssd.free - space - blockSsd - registry.s3 - registry.fs}
                            usable={ssd.total}
                            setSpace={size => setFilesystem(size)}
                            checkBox
                            available={isCheck} />
                </div>
                { hdd && <div className={styles[isHidden ? 'hide' : 'visible']}>
                    <p className={styles.title}>{t('openshift.registryPersistent')}</p>
                    <InputComponent free={ssd.free - space - blockSsd - filesystem}
                            initial={registry.s3 || registry.fs || 0}
                            usable={hdd.total}
                            setSpace={size => setRegistry(size)}
                            options={[
                                { value: '', text: 'None' },
                                { value: 's3', text: 'Cloud S3' },
                                { value: 'fs', text: 'Cloud FS' },
                            ]}
                            available={isCheck} />
                </div> }
            </div>
            <style jsx>{`
                select {
                    width: 110px;
                }
            `}</style>
        </ServiceWrapper>
    )
};

export default memo(OpenshiftConfig);
